import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Tabs, Spin, Empty, Button, Space, Card, App, Tooltip } from 'antd';
import { ArrowLeftOutlined, HomeOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { ProviderSelector } from '../components/detail/ProviderSelector';
import { RawDataTab } from '../components/detail/RawDataTab';
import { PrettyViewTab } from '../components/detail/PrettyViewTab';
import { QualityScoreTab } from '../components/detail/QualityScoreTab';
import { useProductDetail } from '../hooks/useProductDetail';
import { Product, DataSource } from '../types/product';
import { refreshProductFromIcecat, refreshPimProduct, deleteProduct } from '../api/providers';

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();

  // Product can be passed via location state for faster initial render
  const initialProduct = location.state?.product as Product | undefined;

  const [currentProductId, setCurrentProductId] = useState(productId || '');
  const [activeTab, setActiveTab] = useState('pretty');
  const [refreshing, setRefreshing] = useState(false);

  // Always fetch full product data to get quality_metrics (list view only has partial data)
  // Use initialProduct for immediate display while fetching, but prefer fetched data
  const { data: fetchedProduct, isLoading, isError } = useProductDetail(
    currentProductId,
    true // Always fetch to get complete data including quality_metrics
  );

  // Prefer fetched product (has quality_metrics), fall back to initial for loading state
  const product = fetchedProduct || initialProduct;

  useEffect(() => {
    if (productId && productId !== currentProductId) {
      setCurrentProductId(productId);
    }
  }, [productId]);

  const handleProviderChange = (newProductId: string, _provider: DataSource) => {
    setCurrentProductId(newProductId);
    // Update URL to reflect new product
    navigate(`/products/${encodeURIComponent(newProductId)}`, {
      replace: true,
    });
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handleRefresh = async () => {
    if (!product) return;

    setRefreshing(true);
    try {
      const isPim = product.data_source === 'pim' && product.pim_product_id;
      const response = isPim
        ? await refreshPimProduct(product.pim_product_id!)
        : await refreshProductFromIcecat(product.productid);
      if (response.status === 'success') {
        message.success(isPim ? 'Product refreshed from providers' : 'Product refreshed from IceCat');
        window.location.reload();
      } else {
        message.error(response.error || 'Failed to refresh product');
      }
    } catch (error: unknown) {
      console.error('Failed to refresh:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message || 'Failed to refresh';
      message.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = () => {
    if (!product) return;
    modal.confirm({
      title: `Delete ${product.productid}?`,
      content: 'This will remove the cached product data. It will be recreated on next query.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const resp = await deleteProduct(product.productid);
          message.success(`Deleted: ${(resp.deleted || []).join(', ')}`);
          navigate('/products');
        } catch {
          message.error('Failed to delete product');
        }
      },
    });
  };

  if (isLoading && !product) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading product details...</div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <Empty description="Product not found">
        <Button type="primary" onClick={handleBack}>
          Back to Products
        </Button>
      </Empty>
    );
  }

  const tabItems = [
    {
      key: 'pretty',
      label: 'Pretty View',
      children: <PrettyViewTab product={product} />,
    },
    {
      key: 'quality',
      label: 'Quality Score',
      children: <QualityScoreTab product={product} />,
    },
    {
      key: 'raw',
      label: 'Raw Data',
      children: <RawDataTab product={product} />,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <Breadcrumb
          items={[
            {
              href: '/products',
              title: (
                <>
                  <HomeOutlined />
                  <span>Products</span>
                </>
              ),
            },
            {
              title: product.mfgpartno || product.productid,
            },
          ]}
        />
      </div>

      {/* Action Bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Back
            </Button>
            {(product.data_source === 'icecat' || product.data_source === 'pim') && (
              <Tooltip title={product.data_source === 'pim'
                ? `Re-fetch from linked providers: ${(product.source_providers || []).join(', ')}`
                : "Re-fetch this product's data from IceCat API"
              }>
                <Button
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={handleRefresh}
                  loading={refreshing}
                >
                  {product.data_source === 'pim' ? 'Refresh from Providers' : 'Refresh from IceCat'}
                </Button>
              </Tooltip>
            )}
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Space>

          <ProviderSelector
            currentProvider={product.data_source}
            currentProductId={product.productid}
            currentScore={product.quality_score}
            alternatives={product._alternatives}
            onChange={handleProviderChange}
          />
        </div>
      </Card>

      {/* Content Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />
    </div>
  );
}
